import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DialogTitle } from '@headlessui/react';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import Modal from 'components/layout/modals/Modal/Modal';
import { TextInputField } from 'components/forms';
import { PrimaryButton, Button, CloseButton } from 'components/buttons';

export default function CloneReminderModal({
  isOpen,
  onClose,
  onConfirm,
  reminder,
  loading = false,
}) {
  const defaultSubject = reminder?.subject ? `${reminder.subject} (Copy)` : '';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { subject: defaultSubject },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ subject: defaultSubject });
    }
  }, [isOpen, defaultSubject, reset]);

  const submit = (values) => {
    onConfirm(values.subject.trim());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="bg-gray-800">
        <div className="relative px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
          <CloseButton onClick={onClose} className="absolute right-4 top-4" size="sm" />
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-500/10 sm:mx-0 sm:size-10">
              <DocumentDuplicateIcon className="size-6 text-indigo-400" aria-hidden="true" />
            </div>
            <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
              <DialogTitle as="h3" className="text-base font-semibold leading-6 text-white">
                Clone reminder
              </DialogTitle>
              <p className="mt-2 text-sm text-gray-400">
                A new reminder will be created with the same schedule, message,
                assigned worker, todos, and any other attached features.
              </p>
              <div className="mt-4">
                <TextInputField
                  name="subject"
                  labelText="New reminder name"
                  placeholder="Enter a name for the cloned reminder"
                  register={register}
                  errors={errors}
                  registerOptions={{
                    required: 'Reminder name is required',
                    validate: (value) =>
                      (value && value.trim().length > 0) || 'Reminder name is required',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-700/25 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          <PrimaryButton type="submit" saving={loading} disabled={loading} className="sm:ml-3 sm:w-auto">
            {loading ? 'Cloning...' : 'Clone reminder'}
          </PrimaryButton>
          <Button type="button" onClick={onClose} disabled={loading} className="mt-3 sm:mt-0 sm:w-auto">
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
